import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { 
    Banknote, Zap, TrendingUp, Download, PieChart, BarChart3,
    ArrowUpRight, AlertTriangle, CheckCircle, Target, Trophy, Info,
    Map, Truck, Ticket, Calculator
} from 'lucide-react';
import { AppButton, BlackboxLoaderIcon } from '../../components/SharedUI';
import { generateRevenuePotentialReportLLM, generateDetailedStreamingForecastLLM } from '../../services/aiService';

// --- UTILS ---
const formatCurrency = (value) => {
    if (!value || isNaN(value)) return '$0';
    if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
    return `$${value}`;
};

// --- STYLED INPUTS ---
const FinancialInput = ({ label, prefix, ...props }) => (
    <div className="group">
        <label className="block text-[10px] font-bold text-emerald-500/80 uppercase tracking-widest mb-1.5 group-focus-within:text-emerald-400 transition-colors">
            {label}
        </label>
        <div className="relative">
            {prefix && <div className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 font-mono text-sm pointer-events-none">{prefix}</div>}
            <input 
                {...props}
                className={`w-full bg-zinc-900/80 border border-zinc-800 text-white font-mono text-sm px-4 py-3 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500/50 transition-all placeholder:text-zinc-700 shadow-inner ${prefix ? 'pl-8' : ''}`}
            />
        </div>
    </div>
);

const FinancialSelect = ({ label, children, ...props }) => (
    <div className="group">
        <label className="block text-[10px] font-bold text-emerald-500/80 uppercase tracking-widest mb-1.5 group-focus-within:text-emerald-400 transition-colors">
            {label}
        </label>
        <div className="relative">
            <select 
                {...props}
                className="w-full appearance-none bg-zinc-900/80 border border-zinc-800 text-white font-mono text-sm px-4 py-3 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500/50 transition-all cursor-pointer shadow-inner"
            >
                {children}
            </select>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-zinc-600">
                <ArrowUpRight size={14} />
            </div>
        </div>
    </div>
);

// --- SCENARIO CARD ---
const ScenarioCard = ({ type, data, isActive, onClick }) => {
    const colors = {
        conservative: "border-zinc-700 hover:border-zinc-500 bg-zinc-900/50",
        realistic: "border-blue-500/50 hover:border-blue-400 bg-blue-900/10",
        breakout: "border-emerald-500/50 hover:border-emerald-400 bg-emerald-900/10"
    };
    
    const activeClass = isActive ? "ring-2 ring-offset-2 ring-offset-black " + (type === 'breakout' ? 'ring-emerald-500' : type === 'realistic' ? 'ring-blue-500' : 'ring-zinc-500') : "opacity-70 hover:opacity-100";

    return (
        <button onClick={onClick} className={`relative flex-1 p-4 rounded-xl border transition-all duration-300 ${colors[type]} ${activeClass}`}>
            {isActive && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-black px-2 text-[10px] text-white uppercase tracking-widest border border-zinc-800 rounded-full">Active</div>}
            <div className="text-center">
                <p className="text-xs font-bold uppercase tracking-widest mb-1 opacity-80">{type}</p>
                <p className="text-xl md:text-3xl font-black font-mono text-white tracking-tight">
                    {formatCurrency(data?.domestic_box_office + data?.international_box_office)}
                </p>
                <p className="text-[10px] mt-2 leading-tight opacity-60 line-clamp-2 h-8">{data?.rationale}</p>
            </div>
        </button>
    );
};

// --- WATERFALL VISUALIZER ---
const WaterfallChart = ({ gross, marketing, budget, splits, maxBudgetCap }) => {
    const exhibitorCut = gross * (splits.exhibitor_share_percent || 0.5);
    const studioGross = gross - exhibitorCut;
    const distributionFee = studioGross * (splits.distribution_fee_percent || 0.3);
    const netProceeds = studioGross - distributionFee;
    
    const marketingCost = Number(marketing) || 0;
    const productionCost = Number(budget) || 0;
    
    const remainingAfterPA = netProceeds - marketingCost;
    const profit = remainingAfterPA - productionCost;
    
    const isProfitable = profit > 0;
    const roi = productionCost > 0 ? ((netProceeds - marketingCost) / productionCost) * 100 : 0;

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-end">
                <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-widest">Net Producer's Profit</p>
                    <p className={`text-4xl font-black font-mono tracking-tighter ${isProfitable ? 'text-emerald-400' : 'text-red-500'}`}>
                        {profit > 0 ? '+' : ''}{formatCurrency(profit)}
                    </p>
                </div>
                <div className="text-right">
                    <p className="text-xs text-zinc-500 uppercase tracking-widest">ROI</p>
                    <p className={`text-2xl font-bold ${roi > 100 ? 'text-emerald-400' : roi > 0 ? 'text-blue-400' : 'text-red-500'}`}>
                        {roi.toFixed(0)}%
                    </p>
                </div>
            </div>

            {/* Visual Bars */}
            <div className="space-y-2">
                <WaterfallBar label="Total Box Office" value={gross} total={gross} color="bg-zinc-700" />
                <WaterfallBar label="Exhibitor Share" value={exhibitorCut} total={gross} color="bg-red-900/50" isNegative />
                <WaterfallBar label="Distribution Fees" value={distributionFee} total={gross} color="bg-orange-900/50" isNegative />
                <WaterfallBar label="Marketing Recoup" value={marketingCost} total={gross} color="bg-blue-900/50" isNegative />
                <WaterfallBar label="Budget Recoup" value={productionCost} total={gross} color="bg-purple-900/50" isNegative />
                
                {/* The Bottom Line */}
                <div className="pt-2 border-t border-white/10">
                    <div className="flex items-center justify-between text-xs mb-1">
                        <span className="font-bold text-white uppercase">Net Profit</span>
                        <span className="font-mono text-white">{formatCurrency(profit)}</span>
                    </div>
                    <div className="w-full h-2 bg-zinc-800 rounded-full overflow-hidden">
                        {isProfitable && (
                            <div className="h-full bg-emerald-500" style={{ width: `${Math.min((profit / gross) * 100, 100)}%` }} />
                        )}
                    </div>
                </div>
            </div>

            {/* Smart Budget Advice */}
            {maxBudgetCap && (
                <div className={`mt-6 p-4 rounded-xl border flex items-start gap-4 ${productionCost > maxBudgetCap ? 'bg-red-900/10 border-red-500/30' : 'bg-emerald-900/10 border-emerald-500/30'}`}>
                    <div className={`p-2 rounded-lg ${productionCost > maxBudgetCap ? 'bg-red-500/20 text-red-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                        <Calculator size={20} />
                    </div>
                    <div>
                        <p className={`text-xs font-bold uppercase tracking-widest mb-1 ${productionCost > maxBudgetCap ? 'text-red-400' : 'text-emerald-400'}`}>
                            {productionCost > maxBudgetCap ? 'Budget Alert: Over Limit' : 'Budget Status: Viable'}
                        </p>
                        <p className="text-sm text-zinc-300 leading-snug">
                            To maintain safety in a conservative scenario, the AI recommends capping production spend at <span className="font-mono font-bold text-white">{formatCurrency(maxBudgetCap)}</span>.
                            {productionCost > maxBudgetCap 
                                ? <span> You are currently <span className="text-red-400 font-bold">{formatCurrency(productionCost - maxBudgetCap)}</span> over the recommended limit.</span>
                                : <span> You are <span className="text-emerald-400 font-bold">{formatCurrency(maxBudgetCap - productionCost)}</span> under the cap. Good to go.</span>
                            }
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

const WaterfallBar = ({ label, value, total, color, isNegative }) => {
    const percent = Math.min((value / total) * 100, 100);
    return (
        <div className="group relative">
            <div className="flex justify-between text-[10px] text-zinc-400 mb-1 uppercase tracking-wider relative z-10">
                <span>{label}</span>
                <span className="font-mono">{isNegative ? '-' : ''}{formatCurrency(value)}</span>
            </div>
            <div className="w-full h-2 bg-zinc-900 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${color} transition-all duration-500 ease-out`} style={{ width: `${percent}%` }} />
            </div>
        </div>
    );
};

// --- MAIN COMPONENT ---
const RevenueForecasterTab = ({ filmData, setNotification, saveFilmData, credits, deductCredit }) => {
    const [analysisType, setAnalysisType] = useState('theatrical'); 
    const [activeScenario, setActiveScenario] = useState('realistic'); 
    const [loading, setLoading] = useState(false);
    
    const revenueForecast = useMemo(() => filmData.revenue_forecast || null, [filmData.revenue_forecast]);
    
    // Budget initialization logic
    const initialBudget = useMemo(() => {
        if (filmData.revenue_forecast?.inputs?.productionBudget) return filmData.revenue_forecast.inputs.productionBudget;
        if (filmData.budgetLevel === "Micro (<$500k)") return 250000;
        if (filmData.budgetLevel === "Indie ($500k - $5M)") return 2500000;
        if (filmData.budgetLevel === "Mid-Tier ($5M - $50M)") return 20000000;
        return 50000000;
    }, [filmData.budgetLevel, filmData.revenue_forecast]);

    const [inputs, setInputs] = useState({
        marketingSpend: '500000', 
        productionBudget: initialBudget,
        theaterCount: '1500', 
        releaseDate: new Date().toISOString().split('T')[0], 
        targetMarkets: '',
        distributionGoal: 'Maximize Upfront', 
        comparableTitles: '', 
        targetPlatforms: ''
    });

    useEffect(() => {
        if (revenueForecast?.inputs) {
            setInputs(prev => ({...prev, ...revenueForecast.inputs}));
            if(revenueForecast.type) setAnalysisType(revenueForecast.type);
        }
    }, [revenueForecast]);

    const handleGenerate = async () => {
        if (credits < 5) return setNotification({ type: 'error', message: 'Insufficient credits.' });
        setLoading(true);
        try {
            let result;
            if (analysisType === 'theatrical' || analysisType === 'hybrid') {
                result = await generateRevenuePotentialReportLLM(
                    filmData, 
                    inputs.marketingSpend, 
                    inputs.releaseDate, 
                    inputs.theaterCount, 
                    inputs.targetMarkets,
                    inputs.productionBudget,
                    analysisType
                );
            } else {
                result = await generateDetailedStreamingForecastLLM(filmData, inputs.distributionGoal, inputs.comparableTitles, inputs.targetPlatforms);
            }
            await saveFilmData({ ...filmData, revenue_forecast: { type: analysisType, data: result, inputs: inputs } });
            await deductCredit(5);
            setNotification({ type: 'success', message: 'Forecast generated.' });
        } catch (e) {
            setNotification({ type: 'error', message: 'Analysis failed.' });
        } finally {
            setLoading(false);
        }
    };

    const handleUseTopMarkets = useCallback(() => {
        const latestAudience = filmData.ai_analyses?.[filmData.ai_analyses.length - 1]?.analysis;
        if (latestAudience && latestAudience.top_us_theatrical_markets) {
            setInputs(prev => ({...prev, targetMarkets: latestAudience.top_us_theatrical_markets.join(', ')}));
            setNotification({ type: 'success', message: 'Market data synced.' });
        } else {
            setNotification({ type: 'info', message: 'Run Audience Analysis first.' });
        }
    }, [filmData, setNotification]);

    // --- PDF EXPORT ENGINE ---
    const handleExportPDF = () => {
        try {
            if (!window.jspdf || !revenueForecast) {
                setNotification({ type: 'error', message: 'PDF engine not ready.' });
                return;
            }
            
            const { jsPDF } = window.jspdf;
            const doc = new jsPDF('p', 'pt', 'a4');
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const margin = 40;
            let y = margin;

            // --- STUDIO COLORS ---
            const C = {
                EMERALD: [16, 185, 129],
                DARK: [9, 9, 11], // Zinc 950
                GRAY: [113, 113, 122],
                RED: [239, 68, 68],
                BLUE: [59, 130, 246]
            };

            const drawHeader = (title) => {
                if (y + 60 > pageHeight - margin) { doc.addPage(); y = margin; }
                y += 20;
                doc.setFontSize(14);
                doc.setFont("helvetica", "bold");
                doc.setTextColor(...C.EMERALD);
                doc.text(title.toUpperCase(), margin, y);
                doc.setDrawColor(...C.GRAY);
                doc.setLineWidth(0.5);
                doc.line(margin, y + 8, pageWidth - margin, y + 8);
                y += 30;
            };

            // --- PAGE 1: EXECUTIVE DASHBOARD ---
            
            // 1. Cover Header
            doc.setFillColor(...C.DARK);
            doc.rect(0, 0, pageWidth, 140, 'F');
            
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(28);
            doc.setFont("helvetica", "bold");
            doc.text("FINANCIAL INTELLIGENCE", margin, 60);
            
            doc.setFontSize(10);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(200, 200, 200);
            doc.text(`PROJECT: ${filmData.title?.toUpperCase()}`, margin, 90);
            doc.text(`STRATEGY: ${analysisType.toUpperCase()}`, margin, 105);
            
            // Date Stamp
            doc.text(new Date().toLocaleDateString(), pageWidth - margin - 60, 90);

            y = 180;

            // 2. Budget vs. Potential (The Big Numbers)
            const scenario = revenueForecast.data.scenarios?.[activeScenario];
            const gross = scenario ? (scenario.domestic_box_office + scenario.international_box_office) : 0;
            
            doc.setFontSize(10);
            doc.setTextColor(...C.GRAY);
            doc.text("ESTIMATED GLOBAL GROSS (REALISTIC)", margin, y);
            doc.text("PRODUCTION BUDGET", pageWidth / 2 + 10, y);
            y += 25;

            doc.setFontSize(32);
            doc.setFont("courier", "bold");
            doc.setTextColor(...C.EMERALD);
            doc.text(formatCurrency(gross), margin, y);
            
            doc.setTextColor(...C.DARK);
            doc.text(formatCurrency(inputs.productionBudget), pageWidth / 2 + 10, y);
            
            y += 50;

            // 3. Scenario Matrix (Table)
            drawHeader("Scenario Modeling");
            
            const scenarios = revenueForecast.data.scenarios;
            const cols = [
                { name: "CONSERVATIVE", data: scenarios?.conservative, x: margin },
                { name: "REALISTIC", data: scenarios?.realistic, x: margin + 180 },
                { name: "BREAKOUT", data: scenarios?.breakout, x: margin + 360 }
            ];

            const startY = y;
            cols.forEach(col => {
                doc.setFontSize(9);
                doc.setFont("helvetica", "bold");
                doc.setTextColor(...C.GRAY);
                doc.text(col.name, col.x, startY);
                
                if (col.data) {
                    const total = col.data.domestic_box_office + col.data.international_box_office;
                    doc.setFontSize(16);
                    doc.setTextColor(...C.DARK);
                    doc.text(formatCurrency(total), col.x, startY + 20);
                    
                    doc.setFontSize(8);
                    doc.setFont("helvetica", "normal");
                    doc.setTextColor(...C.GRAY);
                    const rationale = doc.splitTextToSize(col.data.rationale || "", 150);
                    doc.text(rationale, col.x, startY + 40);
                }
            });
            y += 100;

            // 4. Profitability Analysis (The New Greenlight Feature)
            if (revenueForecast.data.greenlight_analysis) {
                drawHeader("Profitability Analysis");
                const ga = revenueForecast.data.greenlight_analysis;
                
                doc.setFillColor(245, 245, 245);
                doc.roundedRect(margin, y, pageWidth - (margin * 2), 70, 5, 5, 'F');
                
                doc.setFontSize(10);
                doc.setTextColor(...C.DARK);
                doc.setFont("helvetica", "bold");
                doc.text("RECOMMENDED BUDGET CAP (CONSERVATIVE):", margin + 20, y + 25);
                doc.setFontSize(14);
                doc.setTextColor(...C.EMERALD);
                doc.text(formatCurrency(ga.recommended_budget_cap), margin + 20, y + 45);

                doc.setFontSize(10);
                doc.setTextColor(...C.DARK);
                doc.text("BREAK-EVEN POINT (GROSS):", pageWidth / 2, y + 25);
                doc.text(formatCurrency(ga.break_even_point), pageWidth / 2, y + 45);
                
                y += 90;
            }

            // 5. Recoupment Waterfall (Text Version)
            drawHeader("Recoupment Waterfall");
            const waterfallItems = [
                { l: "Global Box Office", v: formatCurrency(gross), c: C.DARK },
                { l: "Exhibitor Share (~50%)", v: `-${formatCurrency(gross * 0.5)}`, c: C.RED },
                { l: "Marketing (P&A)", v: `-${formatCurrency(inputs.marketingSpend)}`, c: C.RED },
                { l: "Production Budget", v: `-${formatCurrency(inputs.productionBudget)}`, c: C.RED },
                { l: "Est. Net Producer Profit", v: formatCurrency((gross * 0.5) * 0.7 - inputs.marketingSpend - inputs.productionBudget), c: C.EMERALD } // Simplified calculation for PDF
            ];

            waterfallItems.forEach(item => {
                doc.setFontSize(10);
                doc.setFont("helvetica", "bold");
                doc.setTextColor(...C.GRAY);
                doc.text(item.l, margin, y);
                doc.setTextColor(...item.c);
                doc.setFont("courier", "bold");
                doc.text(item.v, pageWidth - margin, y, { align: 'right' });
                doc.setDrawColor(240, 240, 240);
                doc.line(margin, y + 8, pageWidth - margin, y + 8);
                y += 20;
            });

            // Footer
            const pageCount = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.setFontSize(8);
                doc.setTextColor(...C.GRAY);
                doc.text(`BLACKBOX Financial Intelligence | Confidential | Page ${i}`, pageWidth / 2, pageHeight - 20, { align: 'center' });
            }

            doc.save(`${filmData.title?.replace(/\s+/g, '_')}_Financial_Dossier.pdf`);
            setNotification({ type: 'success', message: 'Financial Dossier exported.' });

        } catch (error) {
            console.error("PDF Export Error:", error);
            setNotification({ type: 'error', message: 'PDF Export failed.' });
        }
    };

    const currentScenarioData = revenueForecast?.data?.scenarios?.[activeScenario];

    return (
        <div className="h-full grid grid-cols-1 lg:grid-cols-12 gap-8 pb-20">
            {/* LEFT COLUMN: CONTROL PANEL */}
            <div className="lg:col-span-4 flex flex-col gap-4 h-full overflow-y-auto custom-scrollbar">
                <div className="bg-black/40 backdrop-blur-md border border-white/10 rounded-2xl p-6">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 bg-emerald-500/10 rounded-full flex items-center justify-center border border-emerald-500/20 text-emerald-400">
                            <Banknote size={20} />
                        </div>
                        <h3 className="text-lg font-bold text-white font-poppins">Financial Params</h3>
                    </div>

                    {/* MODE TOGGLE */}
                    <div className="flex p-1 bg-zinc-900 rounded-lg mb-6 border border-zinc-800">
                        <button onClick={() => setAnalysisType('theatrical')} className={`flex-1 py-2 text-[10px] font-bold uppercase rounded-md transition-all ${analysisType === 'theatrical' ? 'bg-zinc-700 text-white shadow' : 'text-zinc-500 hover:text-zinc-300'}`}>Theatrical</button>
                        <button onClick={() => setAnalysisType('hybrid')} className={`flex-1 py-2 text-[10px] font-bold uppercase rounded-md transition-all ${analysisType === 'hybrid' ? 'bg-emerald-900/50 text-emerald-400 shadow border border-emerald-500/20' : 'text-zinc-500 hover:text-zinc-300'}`}>Hybrid/Tour</button>
                        <button onClick={() => setAnalysisType('streaming')} className={`flex-1 py-2 text-[10px] font-bold uppercase rounded-md transition-all ${analysisType === 'streaming' ? 'bg-blue-900/50 text-blue-400 shadow border border-blue-500/20' : 'text-zinc-500 hover:text-zinc-300'}`}>Streaming</button>
                    </div>

                    <div className="space-y-4">
                        <FinancialInput label="Production Budget" prefix="$" type="number" value={inputs.productionBudget} onChange={(e) => setInputs({...inputs, productionBudget: e.target.value})} />
                        <FinancialInput label="Marketing (P&A) Budget" prefix="$" type="number" value={inputs.marketingSpend} onChange={(e) => setInputs({...inputs, marketingSpend: e.target.value})} />
                        
                        {(analysisType === 'theatrical' || analysisType === 'hybrid') && (
                            <>
                                <FinancialInput 
                                    label={analysisType === 'hybrid' ? "Tour Stops / Events" : "Screen Count"} 
                                    prefix="#" 
                                    type="number" 
                                    value={inputs.theaterCount} 
                                    onChange={(e) => setInputs({...inputs, theaterCount: e.target.value})} 
                                />
                                <FinancialInput label="Release Date" type="date" value={inputs.releaseDate} onChange={(e) => setInputs({...inputs, releaseDate: e.target.value})} className="dark:[color-scheme:dark] pl-4" />
                            </>
                        )}
                        
                        {analysisType === 'streaming' && (
                             <FinancialInput label="Target Platforms" placeholder="Netflix, Hulu..." value={inputs.targetPlatforms} onChange={(e) => setInputs({...inputs, targetPlatforms: e.target.value})} />
                        )}
                        
                        <div className="pt-2">
                             <button onClick={handleUseTopMarkets} className="w-full py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-400 text-xs rounded-lg flex items-center justify-center gap-2 border border-zinc-700 transition-colors">
                                 <Map size={12} /> Sync Markets from Audience Intel
                             </button>
                        </div>
                    </div>

                    <div className="mt-8 pt-6 border-t border-white/10">
                        <button 
                            onClick={handleGenerate} 
                            disabled={loading} 
                            className="w-full py-4 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl transition-all shadow-lg shadow-emerald-900/20 flex items-center justify-center gap-2"
                        >
                            {loading ? <BlackboxLoaderIcon className="w-5 h-5" /> : <Zap size={18} fill="currentColor" />}
                            {loading ? 'Running Forecast...' : 'Run Forecast (5)'}
                        </button>
                    </div>
                </div>
            </div>

            {/* RIGHT COLUMN: THE DASHBOARD */}
            <div className="lg:col-span-8 flex flex-col h-full overflow-hidden">
                {!revenueForecast ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-center p-12 bg-black/20 rounded-3xl border border-white/5 border-dashed">
                        <BarChart3 className="w-16 h-16 text-zinc-700 mb-4" />
                        <h3 className="text-2xl font-bold text-white font-poppins">Financial Terminal Offline</h3>
                        <p className="text-zinc-500 max-w-sm mt-2">Enter parameters to initialize the box office simulator.</p>
                    </div>
                ) : (
                    <div className="flex-1 flex flex-col h-full overflow-hidden bg-black/40 backdrop-blur-xl border border-white/10 rounded-3xl shadow-2xl">
                        
                        {/* Header */}
                        <div className="p-6 border-b border-white/5 flex justify-between items-center bg-white/5 flex-shrink-0">
                            <div>
                                <h2 className="text-xl font-bold text-white font-poppins tracking-tight flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_10px_#10b981] animate-pulse" />
                                    REVENUE FORECASTER
                                </h2>
                                <p className="text-[10px] text-zinc-400 uppercase tracking-widest pl-4">
                                    Model: {revenueForecast.type.toUpperCase()} V.2
                                </p>
                            </div>
                            <button 
                                onClick={handleExportPDF}
                                className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-bold uppercase tracking-wide rounded-lg transition-colors border border-zinc-700 hover:border-emerald-500/50"
                            >
                                <Download size={14} /> Export Dossier
                            </button>
                        </div>

                        {/* Scrollable Content */}
                        <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-6">
                            
                            {/* 1. SCENARIO TOGGLES */}
                            {(analysisType === 'theatrical' || analysisType === 'hybrid') && (
                                <div className="flex gap-4">
                                    <ScenarioCard type="conservative" data={revenueForecast.data.scenarios?.conservative} isActive={activeScenario === 'conservative'} onClick={() => setActiveScenario('conservative')} />
                                    <ScenarioCard type="realistic" data={revenueForecast.data.scenarios?.realistic} isActive={activeScenario === 'realistic'} onClick={() => setActiveScenario('realistic')} />
                                    <ScenarioCard type="breakout" data={revenueForecast.data.scenarios?.breakout} isActive={activeScenario === 'breakout'} onClick={() => setActiveScenario('breakout')} />
                                </div>
                            )}

                            {/* 2. MAIN DASHBOARD GRID */}
                            {(analysisType === 'theatrical' || analysisType === 'hybrid') && currentScenarioData && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* LEFT: Stats */}
                                    <div className="space-y-4">
                                        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
                                            <h4 className="text-zinc-400 text-xs font-bold uppercase tracking-widest mb-4">Domestic Performance</h4>
                                            <div className="flex items-baseline gap-2 mb-2">
                                                <span className="text-4xl font-mono font-bold text-white">
                                                    {formatCurrency(currentScenarioData?.domestic_box_office)}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-zinc-500">
                                                <ArrowUpRight size={14} className="text-emerald-500" />
                                                <span>Opening Wknd: {formatCurrency(currentScenarioData?.opening_weekend)}</span>
                                            </div>
                                        </div>

                                        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
                                            <h4 className="text-zinc-400 text-xs font-bold uppercase tracking-widest mb-4">Ancillary Streams</h4>
                                            <p className="text-3xl font-mono font-bold text-white mb-2">
                                                {formatCurrency((currentScenarioData?.domestic_box_office + currentScenarioData?.international_box_office) * (revenueForecast.data.ancillary_revenue_multipliers?.[activeScenario] || 0.5))}
                                            </p>
                                            <p className="text-xs text-zinc-500 leading-snug">
                                                Projected home video, VOD, and licensing revenue.
                                            </p>
                                        </div>
                                    </div>

                                    {/* RIGHT: The Waterfall */}
                                    <div className="bg-black/40 border border-white/10 rounded-xl p-6 relative overflow-hidden">
                                        <div className="absolute top-0 right-0 p-4 opacity-10 pointer-events-none">
                                            <TrendingUp size={100} className="text-white" />
                                        </div>
                                        <h4 className="text-emerald-400 text-xs font-bold uppercase tracking-widest mb-6 flex items-center gap-2">
                                            <PieChart size={14} /> Recoupment Waterfall
                                        </h4>
                                        
                                        <WaterfallChart 
                                            gross={currentScenarioData.domestic_box_office + currentScenarioData.international_box_office}
                                            marketing={inputs.marketingSpend}
                                            budget={inputs.productionBudget} // Uses live input state!
                                            splits={revenueForecast.data.recoupment_structure || {}}
                                            maxBudgetCap={revenueForecast.data.greenlight_analysis?.recommended_budget_cap}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* STREAMING VIEW */}
                            {analysisType === 'streaming' && revenueForecast.data.svod_forecast && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
                                        <h4 className="text-blue-400 font-bold mb-4">SVOD (Subscription)</h4>
                                        <p className="text-3xl font-mono text-white mb-2">{revenueForecast.data.svod_forecast.estimated_revenue}</p>
                                        <p className="text-sm text-zinc-400">{revenueForecast.data.svod_forecast.rationale}</p>
                                    </div>
                                    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
                                        <h4 className="text-cyan-400 font-bold mb-4">AVOD (Ad-Supported)</h4>
                                        <p className="text-3xl font-mono text-white mb-2">{revenueForecast.data.avod_forecast.estimated_revenue}</p>
                                        <p className="text-sm text-zinc-400">{revenueForecast.data.avod_forecast.rationale}</p>
                                    </div>
                                </div>
                            )}

                            {/* 3. COMPS */}
                            {analysisType !== 'streaming' && (
                                <div className="bg-zinc-900/30 border border-zinc-800/50 rounded-xl p-6">
                                    <h4 className="text-zinc-500 text-xs font-bold uppercase tracking-widest mb-4">Comparable Trajectories</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        {revenueForecast.data.comparable_films?.map((comp, i) => (
                                            <div key={i} className="flex items-center justify-between p-3 bg-black/40 rounded-lg border border-white/5">
                                                <span className="font-bold text-sm text-zinc-300">{comp.title}</span>
                                                <div className="text-right">
                                                    <span className="block font-mono text-emerald-400 text-sm">{comp.box_office}</span>
                                                    <span className="block text-[9px] text-zinc-600 uppercase tracking-wider">{comp.scenario_match} Case</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RevenueForecasterTab;